/**
 * Row filters for the AI Models screen, kept as a pure module so they can be unit-tested —
 * the client has no @testing-library/react (jsdom + vitest only), so anything that needs
 * coverage must be renderless. Same convention as components/dataTableFilter.js.
 *
 * These decide what the bulk hide/show acts on, not just what is drawn, so a wrong answer
 * here writes to the database rather than merely looking odd.
 */

export const MODEL_FILTERS = [
  { value: 'all', label: 'All models' },
  { value: 'shown', label: 'Shown only' },
  { value: 'hidden', label: 'Hidden only' },
  { value: 'unpriced', label: 'Unpriced only' },
];

/** MySQL hands `enabled` back as a tinyint, so 0/1 and false/true both reach the client. */
export function isShown(model) {
  return model.enabled !== false && model.enabled !== 0;
}

/**
 * True when this model cannot be costed. BOTH sides must be present: services/chat/pricing.js
 * skips a row priced on only one side (Number(null) is 0, which would silently under-count
 * spend), so a half-priced model is unpriced here too — the two must agree or this screen
 * would report a budget as enforceable when it is not.
 */
export function isUnpriced(model) {
  return model.input_price == null || model.output_price == null;
}

/** @returns rows by identity for 'all' — never a fresh array, so a memo upstream can skip. */
export function applyModelFilter(models, filter) {
  if (!Array.isArray(models)) return [];
  if (filter === 'shown') return models.filter(isShown);
  if (filter === 'hidden') return models.filter((m) => !isShown(m));
  if (filter === 'unpriced') return models.filter(isUnpriced);
  return models;
}

/** The one-line count the header shows, and the numbers the bulk button labels itself with. */
export function summarizeModels(models = []) {
  return {
    total: models.length,
    shown: models.filter(isShown).length,
    unpriced: models.filter((m) => isShown(m) && isUnpriced(m)).length,
  };
}
